import { NgClass, NgStyle } from '@angular/common';
import {
    ChangeDetectionStrategy, Component, computed, inject, input,
} from '@angular/core';
import { Store } from '@ngrx/store';

import { FormattedPriceOptions, FormattedPriceStyles, PricesClasses } from '../../models/product-prices.model';
import { FormattedPriceDetailComponent } from '../formatted-price-detail/formatted-price-detail.component';

import { getPriceFormattedWithAlternates } from '@core/infrastructure/state/core.selectors';
import { CommonPriceCurrenciesFormatted } from '@frontend/common/currency';

@Component({
    selector: 'formatted-price',
    templateUrl: './formatted-price.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NgClass, NgStyle, FormattedPriceDetailComponent],
    standalone: true,
})
export class FormattedPriceComponent {
    private readonly store = inject(Store);

    public readonly price$$ = input<number>();

    public readonly secondaryPrice$$ = input<number>();

    public readonly terciaryPrice$$ = input<number>();

    public readonly lastReducedPrice$$ = input<number>();

    public readonly options$$ = input<FormattedPriceOptions>();

    public readonly pricesClasses$$ = input<PricesClasses>();

    public readonly pricesStyles$$ = input<FormattedPriceStyles>();

    public readonly primaryText$$ = input<string>();

    public readonly secondaryText$$ = input<string>();

    public readonly currency$$ = input<string>();

    public readonly pricesFormatted$$ = computed(() => this.formatPrice(this.price$$()));

    public readonly secondaryPricesFormatted$$ = computed(() => this.formatPrice(this.secondaryPrice$$()));

    // eslint-disable-next-line max-len
    public readonly terciaryPricesFormatted$$ = computed(() => this.formatPrice(this.terciaryPrice$$(), this.lastReducedPrice$$()));

    public readonly lastReducedPriceFormatted$$ = computed(() => this.formatPrice(this.lastReducedPrice$$()));

    public readonly emptyExtraPrices$$ = computed(() =>
        // biome-ignore lint/complexity/useSimplifiedLogicExpression: <explanation>
        !this.secondaryPricesFormatted$$() && !this.terciaryPricesFormatted$$() && !this.lastReducedPriceFormatted$$());

    public readonly primaryPriceContainerClass$$ = computed(() =>
        this.pricesStyles$$()?.cmsStyles?.backgroundColor ? 'd-price-special' : this.pricesClasses$$()?.container);

    public readonly primaryPriceContainerStyles$$ = computed(() => this.getDynamicStyles());

    private formatPrice(price: number, compareChange?: number): CommonPriceCurrenciesFormatted {
        if (!price || price === 0 || price === compareChange) return null;
        return this.store.selectSignal(getPriceFormattedWithAlternates(price, this.currency$$()))();
    }

    private getDynamicStyles(): Record<string, string> {
        const styles = this.pricesStyles$$()?.cmsStyles;
        if (styles?.backgroundColor || styles?.textColor) {
            return {
                'background-color': styles?.backgroundColor || '',
                color: styles?.textColor || '',
            };
        }
        return this.pricesStyles$$()?.primaryPrice || {};
    }
}
